import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(false);
  const [newSocialUser, setNewSocialUser] = useState(false);

  const loadProfile = useCallback(async (uid) => {
    if (!uid) { setProfile(null); return; }
    const { data } = await supabase.from('profiles').select('*').eq('id', uid).maybeSingle();
    setProfile(data || null);
  }, []);

  const refreshProfile = useCallback(() => loadProfile(user?.id), [loadProfile, user?.id]);

  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setUser(session?.user ?? null);
      await loadProfile(session?.user?.id);
      setAuthLoading(false);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      loadProfile(session?.user?.id);
      if (event === 'PASSWORD_RECOVERY') setPasswordRecovery(true);

      if (event === 'SIGNED_IN' && session?.provider_token && session.user?.app_metadata?.provider === 'kakao') {
        supabase.functions.invoke('kakao-auth', {
          body: { access_token: session.provider_token, user_id: session.user.id },
        }).then(({ data, error }) => {
          if (!error) {
            if (data?.is_new) setNewSocialUser(true);
            loadProfile(session.user.id);
          }
        });
      }
    });
    return () => subscription.unsubscribe();
  }, [loadProfile]);

  /* ── 회원가입 (아이디/이름/이메일/비밀번호/휴대폰/주소/생년월일/약관) ── */
  const signUp = ({ username, name, email, password, phone, zip, address1, address2, birthdate, agreeTerms, agreePrivacy, agreeEmail, agreeSms }) =>
    supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          username,
          display_name: name,
          phone, zip, address1, address2, birthdate,
          agree_terms: agreeTerms,
          agree_privacy: agreePrivacy,
          agree_email: agreeEmail,
          agree_sms: agreeSms,
        },
      },
    });

  /* ── 아이디 + 비밀번호 로그인 ── */
  const signIn = async (username, password) => {
    const { data: email, error: lookupErr } = await supabase.rpc('resolve_login_email', { p_username: username.trim() });
    if (lookupErr || !email) {
      return { error: { message: 'Invalid login credentials' } };
    }
    return supabase.auth.signInWithPassword({ email, password });
  };

  const signOut = () => supabase.auth.signOut();

  /* ── 아이디 중복 확인 ── */
  const checkUsernameAvailable = async (username) => {
    const { data, error } = await supabase.rpc('check_username_available', { p_username: username });
    if (error) return false;
    return !!data;
  };

  /* ── 이메일 중복 확인 ── */
  const checkEmailAvailable = async (email) => {
    const { data, error } = await supabase.rpc('check_email_available', { p_email: email });
    if (error) return false;
    return !!data;
  };

  /* ── 아이디 찾기: 이름 + 이메일 ── */
  const findUsername = async (name, email) => {
    const { data, error } = await supabase.rpc('find_username', { p_name: name, p_email: email });
    if (error) return null;
    return data || null;
  };

  /* ── 비밀번호 찾기: 아이디 + 이메일 본인확인 후 재설정 메일 발송 ── */
  const requestPasswordReset = async (username, email) => {
    const { data: ok, error: verifyErr } = await supabase.rpc('verify_account', { p_username: username, p_email: email });
    if (verifyErr || !ok) {
      return { error: { message: '아이디와 이메일이 일치하는 회원 정보를 찾을 수 없습니다.' } };
    }
    return supabase.auth.resetPasswordForEmail(email, { redirectTo: `${window.location.origin}/reset-password` });
  };

  const signInWithKakao = () =>
    supabase.auth.signInWithOAuth({
      provider: 'kakao',
      options: {
        redirectTo: window.location.origin,
        scopes: 'phone_number',
      },
    });

  const signInWithNaver = () => {
    const clientId = process.env.REACT_APP_NAVER_CLIENT_ID;
    if (!clientId) {
      alert('REACT_APP_NAVER_CLIENT_ID 환경변수를 설정하세요. (.env.local)');
      return;
    }
    const state = Math.random().toString(36).slice(2);
    sessionStorage.setItem('naver_state', state);
    const redirect = encodeURIComponent(window.location.origin);
    window.location.href =
      `https://nid.naver.com/oauth2.0/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirect}&state=${state}`;
  };

  return (
    <AuthContext.Provider value={{
      user, profile, authLoading, passwordRecovery, clearPasswordRecovery: () => setPasswordRecovery(false),
      newSocialUser, clearNewSocialUser: () => setNewSocialUser(false),
      signUp, signIn, signOut, signInWithKakao, signInWithNaver,
      checkUsernameAvailable, checkEmailAvailable, findUsername, requestPasswordReset, refreshProfile,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
